import { useCallback, useMemo } from 'react';

import { Item } from '../types/item.types';
import { NON_FILTER_FIELDS } from './filtering.state';
import { SubItem } from '../types/sub-item.types';
import create from 'zustand';
import isEqual from 'lodash/fp/isEqual';
import omit from 'lodash/fp/omit';
import range from 'lodash/range';
import set from 'lodash/fp/set';
import shallow from 'zustand/shallow';
import { useTableDataState } from './table-data.state';

type RowSelections = Record<number, boolean>;
type Selections = Record<number, RowSelections>;

interface SelectionState {
   selections: Selections;
   enforcedSelections: boolean[];
   hideInactive: boolean;
   resetSelection: (pageSize: number) => void;
   toggleHideInactive: (value?: boolean) => void;
}

const STARTING_PAGE_SIZE = 10;

const createEmptySelections = (pageSize: number): Selections =>
   Object.fromEntries(range(pageSize).map((i) => [i, {}]));

const createEmptyEnforcedSelections = (pageSize: number): boolean[] =>
   Array(pageSize).fill(false);

const createRowSelection = (length: number): RowSelections =>
   Object.fromEntries(range(length).map((i) => [i, true]));

const buildSelectAllState = (data: Item[], pageSize: number) => {
   const selections = createEmptySelections(pageSize);
   const enforcedSelections = createEmptyEnforcedSelections(pageSize);

   data.forEach((item, index) => {
      if (item.subItems?.length > 0) {
         selections[index] = createRowSelection(item.subItems.length);
      } else {
         enforcedSelections[index] = true;
      }
   });

   return { selections, enforcedSelections };
};

const getGroupByKey = (item: SubItem): string =>
   `${item.part}${item.materialGrade}${item.partDetail}`;

const getFilterValues = <T extends object>(query: T | null | undefined) =>
   omit(NON_FILTER_FIELDS)(query || {});

export const useSelectionState = create<SelectionState>((set, getState) => ({
   selections: createEmptySelections(STARTING_PAGE_SIZE),
   enforcedSelections: createEmptyEnforcedSelections(STARTING_PAGE_SIZE),
   hideInactive: false,

   resetSelection: (pageSize) => set({
      selections: createEmptySelections(pageSize),
      enforcedSelections: createEmptyEnforcedSelections(pageSize),
   }),

   toggleHideInactive: (val) => set({
      hideInactive: val ?? !getState().hideInactive,
   }),
}));

const initializeTableDataSubscription = (() => {
   let initialized = false;
   let previousFilterValues: object | null = null;

   return () => {
      if (initialized) return;
      if (typeof useTableDataState?.subscribe !== 'function') return;

      initialized = true;

      useTableDataState.subscribe((state) => {
         const currentFilterValues = getFilterValues(state.query);

         const filterChanged = previousFilterValues !== null &&
            !isEqual(previousFilterValues, currentFilterValues);

         if (filterChanged) {
            const pageSize = state.query?.pageSize || STARTING_PAGE_SIZE;
            useSelectionState.getState().resetSelection(pageSize);
         }

         previousFilterValues = currentFilterValues;
      });
   };
})();

const setSelectionsOnRow = (selections: RowSelections, rowIndex: number) =>
   useSelectionState.setState((state) => ({
      selections: set(rowIndex, selections)(state.selections),
   }));

// This hook is used when working with selections on a particular row.
export const useSelectionOnRow = (index: number) => {
   const data = useTableDataState((state) => state.data);

   // only want to update selections when there is a change to this row's selections change (prevents unnecessary re-rendering of each row)
   const selections = useSelectionState(
      (state) => state.selections,
      (oldSel, newSel) => Object.keys(oldSel[index] || {}).length === Object.keys(newSel[index] || {}).length
   );

   const rowSelections = useMemo(
      () => selections[index] || {},
      [selections, index]
   );

   const subItemsLength = data[index]?.subItems?.length || 0;
   const selectedCount = Object.keys(rowSelections).length;

   const isAllSelectedOnRow = subItemsLength > 0 && subItemsLength === selectedCount;
   const isAnySelectedOnRow = selectedCount > 0;

   const setRowSelection = useCallback(
      (updaterOrValue: RowSelections | ((current: RowSelections) => RowSelections)) => {
         const newSelections = typeof updaterOrValue === 'function'
            ? updaterOrValue(rowSelections)
            : updaterOrValue;
         setSelectionsOnRow(newSelections, index);
      },
      [index, rowSelections]
   );

   const selectAllOnRow = useCallback(() => {
      const newSelections = isAllSelectedOnRow
         ? {}
         : createRowSelection(subItemsLength);
      setSelectionsOnRow(newSelections, index);
   }, [index, isAllSelectedOnRow, subItemsLength]);

   return {
      rowSelection: rowSelections,
      setRowSelection,
      isAllSelectedOnRow,
      isAnySelectedOnRow,
      selectAllOnRow,
   };
};

// This is used for the header checkbox for the main table to toggle selections across all expanded tables
export const useSelection = () => {
   const [query, data] = useTableDataState((state) => [state.query, state.data]);
   const [selections, enforcedSelections, resetSelection] = useSelectionState(
      (state) => [
         state.selections,
         state.enforcedSelections,
         state.resetSelection,
      ],
      shallow
   );

   initializeTableDataSubscription();

   const { selectedCount, enforcedCount, isAllSelected } = useMemo(() => {
      let selectedCount = 0;
      let enforcedCount = 0;
      let allSelected = data.length > 0;

      data.forEach((item, index) => {
         const rowKeys = Object.keys(selections[index] || {});
         selectedCount += rowKeys.length;

         if (item.subItems?.length > 0) {
            if (rowKeys.length !== item.subItems.length) {
               allSelected = false;
            }
         } else {
            if (enforcedSelections[index]) {
               enforcedCount++;
            } else {
               allSelected = false;
            }
         }
      });

      return { selectedCount, enforcedCount, isAllSelected: allSelected };
   }, [data, selections, enforcedSelections]);

   const isAnySelected = selectedCount > 0 || enforcedCount > 0;

   const selectAll = useCallback(() => {
      if (isAllSelected) {
         resetSelection(query.pageSize);
      } else {
         useSelectionState.setState(buildSelectAllState(data, query.pageSize));
      }
   }, [data, isAllSelected, query.pageSize, resetSelection]);

   return { selectAll, isAllSelected, isAnySelected };
};

export const useSubItemsFromSelections = () => {
   const data = useTableDataState((state) => state.data);
   const [selections, hideInactive] = useSelectionState((state) => [
      state.selections,
      state.hideInactive,
   ]);

   return useMemo(() => {
      const selectedSubItems: SubItem[] = [];

      data.forEach((item, itemIndex) => {
         if (item.deleted) return;

         const itemSelections = selections[itemIndex];
         if (!itemSelections) return;

         item.subItems.forEach((subItem, subItemIndex) => {
            if (itemSelections[subItemIndex]) {
               selectedSubItems.push(subItem);
            }
         });
      });

      return selectedSubItems
         .filter((subItem) => !hideInactive || subItem.active)
         .sort((a, b) => getGroupByKey(a).localeCompare(getGroupByKey(b)));
   }, [selections, data, hideInactive]);
};

export const useRemoveItemFromSelection = (item: Item) => {
   const data = useTableDataState((state) => state.data);

   return useCallback(() => {
      const index = data.findIndex((i) => i.itemNo === item.itemNo);
      if (index !== -1) {
         setSelectionsOnRow({}, index);
      }
   }, [data, item.itemNo]);
};

export const useParentItemsFromSelectedSubItems = (includeEmpty: boolean) => {
   const data = useTableDataState((state) => state.data);
   const selectedSubItems = useSubItemsFromSelections();
   const [enforcedSelections, selections] = useSelectionState(
      (state) => [state.enforcedSelections, state.selections],
      shallow
   );

   return useMemo(() => {
      const parentItemNumbers = new Set(selectedSubItems.map((s) => s.parentId));

      if (includeEmpty) {
         data.forEach((item, idx) => {
            if (item.deleted) return;

            const isEnforced = enforcedSelections[idx];
            const rawSelectionKeys = Object.keys(selections[idx] || {});

            if (isEnforced || rawSelectionKeys.length > 0) {
               parentItemNumbers.add(item.itemNo);
            }
         });
      }

      return data.filter((item) => parentItemNumbers.has(item.itemNo));
   }, [selectedSubItems, enforcedSelections, selections, data, includeEmpty]);
};